import React, { useRef } from 'react'
import 'react-image-crop/dist/ReactCrop.css'
import ReactCrop from 'react-image-crop'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import Loader from 'components/Loader'
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline'
import { useMergedState } from 'hooks'
import Button from 'components/Button'

const styles = makeStyles(theme => ({
  fileThumbnail: {
    width: `90%`,
    height: `auto`,
    borderRadius: 3,
    overflow: `hidden`,
  },
  fileName: {
    width: `100%`,
    fontWeight: theme.fontWeight[0],
    color: `#8F9BB3`,
    paddingTop: theme.spacing(1.5),
    wordBreak: `break-all`,
    textAlign: `center`,
  },
  error: {
    width: `100%`,
    fontWeight: theme.fontWeight[0],
    color: theme.palette.error.main,
    paddingTop: theme.spacing(1.5),
    wordBreak: `break-all`,
    textAlign: `center`,
  },
  loader: {
    padding: theme.spacing(1.5, 0, 0, 0),
  },
  loaderIcon: {
    width: `20px !important`,
    height: `20px !important`,
    opacity: 0.2,
  },
  addIconUpdate: {
    color: `#98C9FF`,
    width: `3rem`,
    height: `3rem`,
    opacity: 0,
    transition: `all ease 0.3s`,
    position: `absolute`,
    zIndex: 99,
  },
  cropConfirmation: {
    padding: theme.spacing(1.5, 0),
  },
  greenButton: {
    border: `2px solid #4FCA8F`,
    backgroundColor: `#4FCA8F`,
    color: theme.palette.primary.light,
    minWidth: 100,
    maxWidth: `auto`,
    '&:hover': {
      color: `#4FCA8F`,
      backgroundColor: theme.palette.primary.light,
    },
  },
  rejectButton: {
    marginRight: theme.spacing(3),
    border: `2px solid #EF3F44`,
    backgroundColor: theme.palette.primary.light,
    color: `#EF3F44`,
    minWidth: 100,
    maxWidth: `auto`,
    '&:hover': {
      backgroundColor: `#EF3F44`,
      color: theme.palette.primary.light,
    },
  },
  buttons: {
    paddingTop: theme.spacing(1.5),
  },
}))

const baseImagesUrl =
  process.env.REACT_APP_API_BASE_FILE || `https://turismo-back.celulasagiles.keiron.cl/files/`

const pixelRatio = 10

export default function FilesPreviewAndDrop({
  files = null,
  onDeleteFile,
  onAccept,
  fileToSHow = null,
  width = 151,
  height = 100,
}) {
  const classes = styles()
  const [{ crop, croppedImageUrl, croppedBlob }, setState] = useMergedState({
    crop: {
      unit: 'px',
      x: 130,
      y: 50,
      width,
      height,
    },
    croppedBlob: null,
    croppedImageUrl: null,
  })
  const imageRef = useRef(null)
  const fileUrl = useRef(null)

  function onCropChange(crop, percentCrop) {
    setState({ crop: percentCrop })
  }

  function onImageLoaded(image) {
    imageRef.current = image
  }

  function onCropComplete(fileName) {
    return crop => makeClientCrop(crop, fileName)
  }

  function getCroppedImg(crop, fileName) {
    const canvas = document.createElement(`canvas`)
    const image = imageRef.current
    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height

    canvas.width = crop.width * pixelRatio
    canvas.height = crop.height * pixelRatio

    const ctx = canvas.getContext(`2d`)
    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
    ctx.imageSmoothingEnabled = false
    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width,
      crop.height,
    )

    return new Promise((resolve, reject) => {
      canvas.toBlob(blob => {
        if (!blob) {
          return
        }
        blob.name = fileName
        window.URL.revokeObjectURL(fileUrl.current)
        fileUrl.current = window.URL.createObjectURL(blob)
        resolve({
          url: fileUrl.current,
          blob,
        })
      }, `image/jpeg`)
    })
  }

  async function makeClientCrop(crop, fileName) {
    if (imageRef.current && crop.width && crop.height) {
      const { blob, url } = await getCroppedImg(crop, fileName)
      setState({ croppedImageUrl: url, croppedBlob: blob })
    }
  }

  function deleteFile(fileName) {
    onDeleteFile(fileName)
    setState({
      croppedImageUrl: null,
      crop: {
        aspect: 3.02 / 2,
        unit: `%`,
        width,
        height: 50,
        x: 25,
        y: 25,
      },
    })
  }

  function loadImage(fileName) {
    onAccept({
      fileName,
      imageBlob: croppedBlob,
    })
  }

  function renderFileCrop() {
    const { src, loading = false, error = null, name: fileName } = files
    return (
      <Grid container className={classes.file} justify="center">
        {!error && !loading && (
          <ReactCrop
            src={src}
            onImageLoaded={onImageLoaded}
            crop={crop}
            onChange={onCropChange}
            onComplete={onCropComplete(fileName)}
          />
        )}
        {croppedImageUrl && (
          <Grid
            container
            className={classes.cropConfirmation}
            justify="center"
            alignItems="flex-start"
            alignContent={`flex-start`}
          >
            <img src={croppedImageUrl} className={classes.fileThumbnail} alt={`file-${fileName}`} />
            {!loading ? (
              <Grid container justify="center" alignItems="center" className={classes.buttons}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  className={classes.rejectButton}
                  onClick={() => deleteFile(fileName)}
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  className={classes.greenButton}
                  onClick={() => loadImage(fileName)}
                >
                  Aceptar
                </Button>
              </Grid>
            ) : <></>}
          </Grid>
        )}
        {loading && <Loader classes={{ root: classes.loader, icon: classes.loaderIcon }} />}
        {error && (
          <Typography variant="body1" className={classes.error}>
            {error}
          </Typography>
        )}
      </Grid>
    )
  }

  return (
    <>
    <Grid container alignItems="center">
      {files && renderFileCrop()}
      {fileToSHow && (
        <>
        <Grid
          container
          className={classes.cropConfirmation}
          justify="center"
          alignItems="center"
          alignContent={`center`}
        >
          <img
            src={`${baseImagesUrl}${fileToSHow.originalname}`}
            className={classes.fileThumbnail}
            alt={fileToSHow.originalname}
          />
          
          <AddCircleOutlineIcon className={classes.addIconUpdate} />
          {fileToSHow.success && (
            <Typography variant="body1" className={classes.fileName}>
              {fileToSHow.success}
            </Typography>
          )}
          
        </Grid>
          
        </>
      )}
    </Grid>
    
    </>
  )
}
